<template>
  <div class="week-calendar is-unselectable">
    <calendar-controls-bar
      @previous="previous"
      @today="reset"
      @next="next"
      @go-to="goToCustomDate"
    />

    <transition name="slide-top-down" mode="out-in">
      <div class="week" :key="+day.toDate()">
        <div
          class="day"
          :class="{ 'day--current': day.date.isSame(today, 'day') }"
          v-for="day in days"
          :key="day.date.day()"
        >
          <div class="day__header">
            <p
              class="day__header__date"
              :class="{ 'has-text-danger': day.date.day() === 0 }"
            >
              {{ day.date.date() }}
            </p>
            <div>
              <p class="day__header__month">
                {{ day.date.format("MMMM YYYY") }}
              </p>
              <p class="day__header__name">{{ day.date.format("dddd") }}</p>
            </div>
          </div>
          <div class="day__body">
            <div
              class="program-block"
              v-for="block in day.blocks"
              :key="block.id"
            >
              <p class="program-block__name">{{ block.name }}</p>
              <div class="program-block__items">
                <div
                  class="item"
                  v-for="(item, itemIndex) in block.items"
                  :key="itemIndex"
                >
                  <p class="item__controls">
                    <protected :with="Permission.UPDATE_PROGRAM_ITEM">
                      <icon
                        name="fa-pen"
                        @click="
                          editProgramItem(
                            day.date.format('YYYY-MM-DD'),
                            block,
                            item
                          )
                        "
                      />
                    </protected>
                    <protected :with="Permission.DELETE_PROGRAM_ITEM">
                      <icon
                        name="fa-trash"
                        class="control--delete"
                        @click="deleteProgramItem(item)"
                      />
                    </protected>
                  </p>
                  <div>
                    <p class="item__time">
                      {{ item.beginTime.substr(0, 5) }} &ndash;
                      {{ item.endTime.substr(0, 5) }}
                    </p>
                    <p class="item__title">{{ item.title }}</p>
                  </div>
                </div>
                <protected :with="Permission.CREATE_PROGRAM_ITEM">
                  <div
                    class="item item--add"
                    @click="
                      newProgramItem(day.date.format('YYYY-MM-DD'), block)
                    "
                  >
                    <icon name="fa-plus-circle" />
                  </div>
                </protected>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import dayjs from "dayjs";
import get from "lodash.get";

import { Icon } from "%helpers";

import { Protected } from "%mixins";

import CalendarControlsBar from "%admin/components/global/CalendarControlsBar";

import { omit } from "%utils/object";

export default {
  name: "week-calendar",

  mixins: [Protected],

  components: {
    Icon,
    CalendarControlsBar
  },

  props: {
    program: Array,
    blocks: Array,
    delta: Number,
    day: dayjs
  },

  data() {
    return {
      today: dayjs()
    };
  },

  computed: {
    days() {
      return Array.from(Array(this.delta).keys())
        .map(x => this.day.add(x, "day"))
        .map(date => ({
          date,
          blocks: this.getBlocksForDate(date)
        }));
    }
  },

  methods: {
    getBlocksForDate(date) {
      const blocks =
        get(
          this.program.find(d => d.date === date.format("YYYY-MM-DD")),
          "blocks"
        ) || [];


      return this.blocks.map((block, index) => ({
        id: block.id,
        name: block.name,
        items: get(blocks.find(b => b.id === block.id), 'items') || []
      }));
    },

    previous() {
      this.$emit("go-to", this.day.subtract(this.delta, "day"));
    },

    next() {
      this.$emit("go-to", this.day.add(this.delta, "day"));
    },

    reset() {
      this.$emit("go-to", this.today);
    },

    goToCustomDate(date) {
      this.$emit("go-to", date);
    },

    newProgramItem(date, block) {
      this.$emit("new-program-item", { date, block: omit(block, "items") });
    },

    editProgramItem(date, block, item) {
      this.$emit("edit-program-item", {
        date,
        block: omit(block, "items"),
        item
      });
    },

    deleteProgramItem(id) {
      this.$emit("delete-program-item", id);
    }
  }
};
</script>

<style lang="scss" scoped>
$color: darken($white, 4);

.week {
  display: flex;
  justify-content: space-between;
  flex-flow: row wrap;

  .day {
    flex-grow: 1;
    flex-basis: 15rem;
    border: solid 1px $color;
    border-radius: $radius;
    margin: 0.5rem;

    &:empty {
      height: 0;
      border: none;
    }

    &--current {
      .day__header {
        color: $primary;
      }
    }

    &__header {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;
      align-items: flex-start;
      padding: 1rem;

      &__month {
        font-size: 0.7rem;
        text-align: right;
      }

      &__date {
        font-weight: 700;
        font-size: 3rem;
        line-height: 1;
      }

      &__name {
        font-size: 0.7rem;
        text-align: right;
      }
    }

    &__body {
      .program-block {
        font-size: 0.7rem;
        margin-top: 1rem;

        &:not(:first-child) {
          margin-top: 2rem;
        }

        &__name {
          font-weight: 700;
          margin-left: 1rem;
        }

        .item {
          background: $color;
          padding: 0.5rem 1rem;
          display: flex;
          flex-flow: row nowrap;

          &:not(:last-child) {
            margin-bottom: 2px;
          }

          &--add {
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 1rem;
            color: $grey-light;
            cursor: pointer;

            &:hover {
              color: $grey;
              background: darken($color, 5);
            }
          }

          &__controls {
            margin-right: 1rem;
            display: flex;
            flex-flow: column nowrap;
            justify-content: space-between;
            align-items: center;

            .icon {
              color: $grey-light;
              cursor: pointer;

              &:hover {
                color: $grey;
              }

              &.control--delete:hover {
                color: $danger;
              }
            }
          }

          &__time {
            font-family: $family-sans-serif;
          }

          &__title {
            font-family: $family-sans-serif-condensed;
          }
        }
      }
    }
  }
}
</style>
